import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { SCALE_DEFAULT, SCALE_MAX, SCALE_MIN } from '../../../../game/Constants';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { getViewportSize } from '../../../Responsive';
import { beginSliderDragCursor, endSliderDragCursor } from '../../slider-drag-cursor';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

const ZOOM_STEP = 100;
const WHEEL_STEP = 200;

/** Vertical gap between the chip bottom and the popover top. Matches the
 *  original `top: 36px` offset (chip is ~26px tall, leaving ~10px gap). */
const POPOVER_TOP_GAP = 10;

/** Must match `.popover { width: 80px }` below — the full rendered box, since
 *  the popover is border-box. Used to clamp the popover inside the viewport
 *  when the chip is near the right edge. (It was once 64 against a content-box
 *  64px width, which left the 16px of horizontal padding unaccounted for and
 *  let the popover hang 8px off the right edge.) */
const POPOVER_WIDTH = 80;

/** Minimum gap between popover and viewport edges. */
const POPOVER_VIEWPORT_MARGIN = 8;

/** Conservative full height of the open popover (160px track plus labels,
 *  value, reset, padding and gaps). Used only to clamp the popover above the
 *  bottom edge on short landscape viewports — an over-estimate just nudges it
 *  up a touch sooner. Keep roughly in sync with the `.popover` content. */
const POPOVER_ESTIMATED_HEIGHT = 260;

/**
 * Zoom Slider - Quick-access vertical zoom slider beside the game selector.
 *
 * Closed state shows a small magnifier icon button. Opening reveals a popover
 * with a vertical slider bound to the main scaling factor (`zoom.scalingFactor`).
 *
 * Top of track = zoomed in (low value, +), bottom = zoomed out (high value, -).
 * Editing forces alternate-zoom mode off so the change is always visible.
 *
 * @element zoom-slider
 */
@customElement('zoom-slider')
export class ZoomSlider extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        position: relative;
        /* One below game-selector (z-index: 10) so that selector's open dropdown
         * paints over the magnifier icon when its panel extends rightward. */
        z-index: 5;
        font-size: var(--font-size-13);
        color: var(--color-text-white);
        text-shadow: var(--shadow-text);
        pointer-events: auto;
        flex: 0 0 auto;
        /* iOS Safari occasionally fires text selection / drag-preview on
           custom-drag elements despite the body's globally-set
           user-select:none. Re-asserting these on the slider stops the blue
           highlight that pops up while dragging the thumb. */
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
        -webkit-user-drag: none;
      }

      .icon-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 26px;
        padding: 0;
        background-color: var(--color-bg-chip);
        color: var(--color-text-tooltip);
        border: none;
        border-radius: var(--radius-md);
        cursor: pointer;
        outline: none;
      }

      @media (hover: hover) {
        .icon-button:hover {
          color: var(--color-text-white);
          background-color: var(--color-bg-selector-hover);
        }
      }

      .icon-button:active {
        color: var(--color-text-white);
        background-color: var(--color-bg-selector-hover);
      }

      .icon {
        width: 16px;
        height: 16px;
        background-color: currentColor;
        mask-image: url('/vanilla/ui/gameplay/eye.svg');
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-image: url('/vanilla/ui/gameplay/eye.svg');
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }

      /* Touch: finger-sized 44×44 chip matching the other mobile HUD chips
         (loadout, scoreboard). Desktop keeps the compact 30×26 mouse target. */
      @media (hover: none) {
        .icon-button {
          width: 44px;
          height: 44px;
        }
        .icon {
          width: 20px;
          height: 20px;
        }
      }

      /* position:fixed escapes airmash-game's top-bar stacking context (which
         would otherwise trap us at z-index 5 below other in-game UI at
         z 20-50). Still below airmash-ui's panel-overlay (z 300 in a sibling
         stacking context), so modals naturally win. left/top set inline from
         the chip's getBoundingClientRect(). */
      .popover {
        position: fixed;
        z-index: 200;
        /* border-box so this width is the whole rendered box — POPOVER_WIDTH
           above clamps against it. Content is still 64px inside the padding. */
        box-sizing: border-box;
        width: 80px;
        background-color: var(--color-bg-panel);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        padding: 10px 8px 8px;
        cursor: default;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }

      .label {
        font-size: var(--font-size-xs);
        color: var(--color-text-muted);
        line-height: 1;
      }

      .label.top {
        font-weight: bold;
        font-size: var(--font-size-md);
      }

      .track {
        position: relative;
        width: 8px;
        height: 160px;
        background-color: var(--color-slider-track);
        border-radius: var(--radius-sm);
        cursor: pointer;
        /* iOS hands the gesture straight to our pointer handler instead of
           waiting to disambiguate scroll/zoom. Required because the body's
           touch-action:none would otherwise propagate. */
        touch-action: none;
      }

      .fill {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background-color: var(--color-btn-play);
        border-radius: var(--radius-sm);
        pointer-events: none;
      }

      .thumb {
        position: absolute;
        left: 50%;
        width: 16px;
        height: 16px;
        background-color: var(--color-slider-thumb);
        border-radius: 50%;
        box-shadow: var(--shadow-control);
        transform: translate(-50%, -50%);
        cursor: grab;
        transition: transform 0.1s ease;
      }

      /* Invisible hit-zone extension on touch devices — finger-friendly
         44×44 target around the 16×16 visual thumb. Bubbles up to the
         track's @pointerdown handler. Desktop mouse keeps the precise
         16×16 click area. */
      @media (hover: none) {
        .thumb::before {
          content: '';
          position: absolute;
          inset: -14px;
        }
      }

      @media (hover: hover) {
        .thumb:hover {
          transform: translate(-50%, -50%) scale(1.1);
        }
      }

      .thumb:active {
        cursor: grabbing;
        transform: translate(-50%, -50%) scale(1.15);
      }

      .value {
        font-size: var(--font-size-xs);
        color: var(--color-text-white);
        font-weight: 500;
        min-height: 14px;
      }

      .reset {
        position: relative;
        width: 22px;
        height: 22px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--color-bg-btn-ghost);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        margin-top: 2px;
      }

      @media (hover: hover) {
        .reset:hover {
          background: var(--color-bg-btn-ghost-hover);
        }

        .reset:hover .reset-icon {
          background-color: var(--color-text-white);
        }
      }

      .reset:active {
        background: var(--color-bg-btn-ghost-active);
      }

      /* Pad the 22px button to finger size on touch, matching the chip (which
         grows to 44px) and the thumb's own pad. Upward it only reaches the
         read-only value text, so it can't steal a drag from the track. */
      @media (hover: none) {
        .reset::after {
          content: '';
          position: absolute;
          inset: -11px;
        }
      }

      .reset-icon {
        width: 12px;
        height: 12px;
        background-color: var(--color-text-secondary);
        mask-image: url('/vanilla/ui/menu/rotate-ccw.svg');
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        -webkit-mask-image: url('/vanilla/ui/menu/rotate-ccw.svg');
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  @state()
  private isOpen = false;

  /** Viewport-relative position for the fixed popover, computed from the
   *  chip's bounding rect when we open and on resize. */
  @state() private popoverLeft = 0;
  @state() private popoverTop = 0;

  private isDragging = false;
  /** Pointer ID that started the current drag — used to ignore stray
   *  pointermove/up events from other pointers (e.g. a second finger). */
  private activePointerId: number | null = null;

  /** Scaling factor when the current drag began, and the latest previewed
   *  value, so an interrupted drag (cancel / blur / unmount) can commit what
   *  the user actually saw instead of letting it snap back on reload. */
  private dragStartValue = 0;
  private latestPreviewValue = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('resize', this.handleResize);
    // iOS collapses its toolbar without firing a window resize, so an open
    // popover would keep a clamp measured against chrome that is gone.
    window.visualViewport?.addEventListener('resize', this.handleResize);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('resize', this.handleResize);
    window.visualViewport?.removeEventListener('resize', this.handleResize);
    // Commit and tear down any in-flight drag. The preview already changed
    // the visible zoom, so committing keeps it from snapping back on reload;
    // endDrag also releases the stuck drag cursor and removes the blur
    // listener. No-op when not dragging.
    this.endDrag(true);
    // Release the top-bar panel slot if we still own it. Without this, an
    // unmount-while-open keeps `openTopBarPanel === 'zoom-slider'` set and
    // other top-bar panels can't open.
    if (this.isOpen) this.close();
  }

  /** Re-snap the fixed popover to the chip after orientation/resize. */
  private handleResize = () => {
    if (this.isOpen) this.recomputePopoverPosition();
  };

  private recomputePopoverPosition(): void {
    const rect = this.getBoundingClientRect();
    // The visible area, not window.inner* — on iOS the layout viewport stays
    // tall while Safari's toolbar covers the bottom, and the short-landscape
    // case below is exactly where that difference bites.
    const viewport = getViewportSize();
    // Clamp horizontally so the popover stays inside the viewport even when
    // the chip is near the right edge of a cramped top-bar.
    this.popoverLeft = Math.max(
      POPOVER_VIEWPORT_MARGIN,
      Math.min(rect.left, viewport.width - POPOVER_WIDTH - POPOVER_VIEWPORT_MARGIN),
    );
    // Clamp vertically too — the popover is tall (160px track plus labels and
    // reset), so on short landscape phones it would otherwise spill below the
    // fold. On tall viewports the min() leaves the natural below-chip anchor
    // untouched.
    this.popoverTop = Math.max(
      POPOVER_VIEWPORT_MARGIN,
      Math.min(rect.bottom + POPOVER_TOP_GAP, viewport.height - POPOVER_ESTIMATED_HEIGHT - POPOVER_VIEWPORT_MARGIN),
    );
  }

  private handleClickOutside = (e: MouseEvent): void => {
    if (!this.isOpen) return;
    const path = e.composedPath();
    if (!path.includes(this)) {
      this.close();
    }
  };

  /** Close ourselves when a sibling top-bar panel becomes the active one. */
  protected override willUpdate(): void {
    if (this.isOpen && this.uiState.openTopBarPanel !== 'zoom-slider') {
      this.close();
    }
  }

  private toggleOpen = (e: Event): void => {
    e.stopPropagation();
    this.gameApi.playClick();
    if (this.isOpen) {
      this.close();
    } else {
      // Compute coords BEFORE flipping isOpen so the fixed popover paints at
      // the right spot on its first frame.
      this.recomputePopoverPosition();
      this.isOpen = true;
      this.gameApi.setOpenTopBarPanel('zoom-slider');
    }
  };

  /** Shared close path used by outside-click, sibling-takeover (willUpdate),
   *  toggleOpen-when-already-open, and disconnect. The setOpenTopBarPanel
   *  null is guarded so the willUpdate path (where a sibling already grabbed
   *  the slot) doesn't wipe their claim. */
  private close(): void {
    this.isOpen = false;
    if (this.uiState.openTopBarPanel === 'zoom-slider') {
      this.gameApi.setOpenTopBarPanel(null);
    }
  }

  private snapClamp(n: number): number {
    const snapped = Math.round(n / ZOOM_STEP) * ZOOM_STEP;
    return Math.max(SCALE_MIN, Math.min(SCALE_MAX, snapped));
  }

  private ensureMainMode(): void {
    if (this.gameApi.getScalingAltMode()) {
      this.gameApi.toggleScalingAltMode();
    }
  }

  /** Live-preview during drag — cheap, applies to graphics without persisting.
   *  Remembers the value so an interrupted drag can commit what was previewed. */
  private previewValue(value: number): void {
    this.ensureMainMode();
    const snapped = this.snapClamp(value);
    this.latestPreviewValue = snapped;
    this.gameApi.applyScalingFactor(snapped);
  }

  /** Commit on release / wheel / reset — persists and notifies server */
  private commitValue(value: number): void {
    this.ensureMainMode();
    this.gameApi.setScalingFactor(this.snapClamp(value));
  }

  private valueFromY(clientY: number, rect: DOMRect): number {
    if (rect.height <= 0) return SCALE_DEFAULT;
    const pct = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    return SCALE_MIN + pct * (SCALE_MAX - SCALE_MIN);
  }

  /** Pointer-based drag — works for mouse, touch, and pen on every browser.
   *  setPointerCapture routes subsequent move/up events to the track even
   *  if the pointer leaves it mid-drag, so we don't need document-level
   *  listeners. */
  private handleTrackPointerDown = (e: PointerEvent): void => {
    // Left-button guard applies to mouse only — touch/pen report button 0 on
    // contact. Matches range-slider's guard.
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0) || this.activePointerId !== null) return;
    e.stopPropagation();
    e.preventDefault();
    const track = e.currentTarget as HTMLElement;
    // Claim the pointer before touching any state. setPointerCapture throws
    // NotFoundError if the pointer is no longer active, and since this drag
    // relies on capture for its move/up events, a throw part-way through setup
    // would strand isDragging/activePointerId with nothing left to clear them —
    // after which the guard above rejects every future press.
    try {
      track.setPointerCapture(e.pointerId);
    } catch {
      return;
    }
    this.isDragging = true;
    this.activePointerId = e.pointerId;
    this.dragStartValue = this.snapClamp(this.uiState.zoom.scalingFactor);
    this.latestPreviewValue = this.dragStartValue;
    beginSliderDragCursor();
    // Backstop for focus loss mid-drag (alt-tab, app-switch): pointercancel
    // isn't guaranteed to fire, so guarantee teardown + commit via blur.
    window.addEventListener('blur', this.handleDragInterrupt);
    this.previewValue(this.valueFromY(e.clientY, track.getBoundingClientRect()));
  };

  private handleTrackPointerMove = (e: PointerEvent): void => {
    if (!this.isDragging || e.pointerId !== this.activePointerId) return;
    e.preventDefault();
    const track = e.currentTarget as HTMLElement;
    this.previewValue(this.valueFromY(e.clientY, track.getBoundingClientRect()));
  };

  private handleTrackPointerUp = (e: PointerEvent): void => {
    if (!this.isDragging || e.pointerId !== this.activePointerId) return;
    e.preventDefault();
    const track = e.currentTarget as HTMLElement;
    if (track.hasPointerCapture(e.pointerId)) {
      track.releasePointerCapture(e.pointerId);
    }
    if (e.type === 'pointercancel') {
      // System aborted the gesture (e.g. a system gesture interrupted it).
      // The preview already changed the visible zoom, so commit the last
      // previewed value — not the cancel event's possibly-stale coordinates —
      // so what the user sees matches what's saved.
      this.endDrag(true);
    } else {
      // Normal release: commit the release position. A fast flick can coalesce
      // the final move into the up event, so read clientY fresh here rather
      // than trusting the last pointermove.
      this.commitValue(this.valueFromY(e.clientY, track.getBoundingClientRect()));
      this.endDrag(false);
    }
  };

  /** Tear down drag state and release the drag cursor; when
   *  `commit` is set and the value actually moved, persist the last previewed
   *  value. The cancel / blur / unmount paths pass `true` because their
   *  preview already changed the visible zoom but the normal pointerup commit
   *  never ran — committing keeps visible and saved in sync. No-op when not
   *  dragging. */
  private endDrag(commit: boolean): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.activePointerId = null;
    endSliderDragCursor();
    window.removeEventListener('blur', this.handleDragInterrupt);
    if (commit && this.latestPreviewValue !== this.dragStartValue) {
      this.commitValue(this.latestPreviewValue);
    }
  }

  /** Window blur mid-drag (alt-tab, app-switch, OS gesture). Backstops
   *  pointercancel, which isn't guaranteed on focus loss. */
  private handleDragInterrupt = (): void => {
    this.endDrag(true);
  };

  private handleWheel = (e: WheelEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    const current = this.uiState.zoom.scalingFactor;
    const delta = e.deltaY > 0 ? WHEEL_STEP : -WHEEL_STEP;
    this.commitValue(current + delta);
  };

  private handleReset = (e: Event): void => {
    e.stopPropagation();
    this.gameApi.playClick();
    this.commitValue(SCALE_DEFAULT);
  };

  /** Stable identity for the popover's click trap so Lit doesn't rebind on
   *  every render — matches the pattern used in game-selector. */
  private stopClickPropagation = (e: Event): void => {
    e.stopPropagation();
  };

  override render() {
    const value = this.uiState.zoom.scalingFactor;
    // Clamp so fill/thumb never render outside the track if state ever drifts
    // out of [SCALE_MIN, SCALE_MAX].
    const pct = Math.max(0, Math.min(100, ((value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100));
    const showReset = value !== SCALE_DEFAULT;
    // The saved value is shown; the game applies it no wider than the server's cap
    const cap = this.uiState.zoom.serverCap;
    const title =
      cap !== null && value > cap ? `Zoom level (${value}, this server allows up to ${cap})` : `Zoom level (${value})`;

    return html`
      <button class="icon-button" @click=${this.toggleOpen} title=${title}>
        <span class="icon" aria-hidden="true"></span>
      </button>
      ${
        this.isOpen
          ? html`
              <div
                class="popover"
                style=${styleMap({ left: `${this.popoverLeft}px`, top: `${this.popoverTop}px` })}
                @click=${this.stopClickPropagation}
              >
                <span class="label top">+</span>
                <div
                  class="track"
                  @pointerdown=${this.handleTrackPointerDown}
                  @pointermove=${this.handleTrackPointerMove}
                  @pointerup=${this.handleTrackPointerUp}
                  @pointercancel=${this.handleTrackPointerUp}
                  @wheel=${this.handleWheel}
                >
                  <div class="fill" style="height: ${pct}%"></div>
                  <div class="thumb" style="top: ${pct}%"></div>
                </div>
                <span class="label">−</span>
                <span class="value">${value}</span>
                ${
                  showReset
                    ? html`
                        <button class="reset" @click=${this.handleReset} title="Reset to ${SCALE_DEFAULT}">
                          <span class="reset-icon"></span>
                        </button>
                      `
                    : nothing
                }
              </div>
            `
          : nothing
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'zoom-slider': ZoomSlider;
  }
}
